import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, X, CheckCircle2, AlertCircle } from 'lucide-react';
import theme from '../theme';

/**
 * Real coach-mark guided tour. Highlights the actual UI element behind a translucent
 * scrim, then renders a tooltip next to it with Step X of Y / Back / Next / Skip.
 * If the target testid isn't present within 1500ms it falls back to a centered
 * "Target not found" tooltip but still lets the user advance.
 */

const STEPS_BY_ROLE = {
  creator: [
    { title: 'Active Kickbacks card',  body: 'This card shows how many of your Kickbacks are live and listed.',          to: '/creator/dashboard',                  targetTestId: 'creator-summary-active',         placement: 'bottom' },
    { title: 'Contracts remaining card', body: 'Total contract drafts available against your plan quota.',                  to: '/creator/dashboard',                  targetTestId: 'creator-summary-contracts',      placement: 'bottom' },
    { title: 'Create Portfolio button',  body: 'Start the portfolio wizard from here — name, kickback %, units, utilities.',to: '/creator/dashboard',                  targetTestId: 'creator-btn-create-portfolio',   placement: 'left'   },
    { title: 'Contract Templates link',  body: 'Pick an ERC-721 / 1155 / 20 template to spin up a new draft.',              to: '/creator/contracts/templates',        targetTestId: 'creator-nav-contract-templates', placement: 'right'  },
    { title: 'Utilities Marketplace',    body: 'Browse listings from other creators and attach them to your portfolios.',   to: '/creator/utilities-marketplace',      targetTestId: 'creator-nav-utilities-marketplace', placement: 'right' },
    { title: 'Payouts page',             body: 'Real revenue, sales count and per-portfolio breakdown — every row is clickable.', to: '/creator/payouts',               targetTestId: 'creator-nav-payouts',            placement: 'right'  },
    { title: 'Settings & payout prefs',  body: 'Configure ACH / PayPal / crypto split here. The Reset Tour button lives in this page.', to: '/creator/settings',           targetTestId: 'creator-nav-settings',           placement: 'right'  },
  ],
  buyer: [
    { title: 'Marketplace tab',  body: 'Switch to Holdings/Marketplace to browse listings.',           to: '/buyer/dashboard', targetTestId: 'buyer-nav-marketplace', placement: 'bottom' },
    { title: 'Owned NFT card',   body: 'Click any of your owned NFTs to open its detail.',             to: '/buyer/dashboard', targetTestId: 'buyer-card-nft-0',      placement: 'bottom' },
    { title: 'Transfers',        body: 'Track pending and completed transfers here.',                   to: '/buyer/dashboard', targetTestId: 'buyer-nav-transfers',   placement: 'bottom' },
    { title: 'Utilities',        body: 'Inspect attached utilities and the provider behind each one.',  to: '/buyer/dashboard', targetTestId: 'buyer-nav-utilities',   placement: 'bottom' },
    { title: 'Payouts',          body: 'Your kickback payout history.',                                 to: '/buyer/dashboard', targetTestId: 'buyer-nav-payouts',     placement: 'bottom' },
    { title: 'Settings',         body: 'Wallets, profile, payout preferences. Reset Tour button is here.', to: '/buyer/dashboard', targetTestId: 'buyer-nav-settings', placement: 'bottom' },
  ],
  broker: [
    { title: 'Open opportunities', body: 'These are open partner opportunities you can accept or decline.', to: '/broker/dashboard', targetTestId: 'broker-opportunity-0', placement: 'bottom' },
    { title: 'Active portfolios',  body: 'Portfolios you are actively brokering live in this tab.',         to: '/broker/portfolios', targetTestId: 'broker-nav-portfolios', placement: 'right' },
    { title: 'Directory',          body: 'Showcase accepted portfolios on your public broker directory profile.', to: '/broker/directory', targetTestId: 'broker-nav-directory', placement: 'right' },
    { title: 'Disputes',           body: 'Disputes related to portfolios you broker land here.',            to: '/broker/disputes',   targetTestId: 'broker-nav-disputes',   placement: 'right' },
    { title: 'Payouts',            body: 'Track your broker commissions and payment history.',              to: '/broker/payouts',    targetTestId: 'broker-nav-payouts',    placement: 'right' },
    { title: 'Settings',           body: 'Manage specialties, contact info and Reset Tour.',                to: '/broker/settings',   targetTestId: 'broker-nav-settings',   placement: 'right' },
  ],
  admin: [
    { title: 'Total users KPI',   body: 'Top-level user count across all roles.',                                       to: '/admin/dashboard?tab=overview',   targetTestId: 'admin-kpi-total-users',  placement: 'bottom' },
    { title: 'Invites tab',       body: 'Generate invite tokens for buyers and admins.',                                to: '/admin/dashboard?tab=invites',    targetTestId: 'admin-tab-invites',      placement: 'bottom' },
    { title: 'Disputes triage',   body: 'Change status, add admin notes, resolve buyer/creator disputes.',              to: '/admin/dashboard?tab=disputes',   targetTestId: 'admin-tab-disputes',     placement: 'bottom' },
    { title: 'Users tab',         body: 'Inspect and edit users across all roles.',                                     to: '/admin/dashboard?tab=users',      targetTestId: 'admin-tab-users',        placement: 'bottom' },
    { title: 'Portfolios tab',    body: 'Approve or suspend portfolios.',                                                to: '/admin/dashboard?tab=portfolios', targetTestId: 'admin-tab-portfolios',   placement: 'bottom' },
    { title: 'Plans & Pricing',   body: 'Manage subscription plans and feature gates.',                                  to: '/admin/dashboard?tab=plans',      targetTestId: 'admin-tab-plans',        placement: 'bottom' },
  ],
};

export const guidedTourKey = (userId, role) => `tour_done:${userId || 'anon'}:${role || 'unknown'}`;
export const guidedTourSkipKey = (userId, role) => `tour_skipped:${userId || 'anon'}:${role || 'unknown'}`;

const SCRIM = 'rgba(15, 22, 37, 0.55)';

function computeTooltipPos(rect, placement) {
  const W = window.innerWidth, H = window.innerHeight;
  const tw = 360, th = 200;
  const GAP = 16;

  // BUG #38 — pick the placement with the most free space so the tooltip
  // never covers the target. Any explicit placement from the step config
  // is only honoured when it actually fits.
  const space = {
    bottom: H - rect.bottom - GAP,
    top:    rect.top - GAP,
    right:  W - rect.right - GAP,
    left:   rect.left - GAP,
  };
  const ORDER = ['bottom', 'top', 'right', 'left'];
  let chosen = placement && space[placement] >= th ? placement : null;
  if (!chosen) {
    // Prefer vertical placements first (bottom, then top) so the target
    // control stays fully visible above/below.
    chosen = ORDER
      .filter((p) => (p === 'left' || p === 'right') ? space[p] >= tw : space[p] >= th)
      .sort((a, b) => space[b] - space[a])[0]
      || ORDER.sort((a, b) => space[b] - space[a])[0]
      || 'bottom';
  }

  let top = 0, left = 0;
  switch (chosen) {
    case 'top':    top = Math.max(8, rect.top - th - GAP); left = rect.left + rect.width / 2 - tw / 2; break;
    case 'left':   top = rect.top + rect.height / 2 - th / 2; left = Math.max(8, rect.left - tw - GAP); break;
    case 'right':  top = rect.top + rect.height / 2 - th / 2; left = Math.min(W - tw - 8, rect.right + GAP); break;
    case 'bottom':
    default:       top = Math.min(H - th - 8, rect.bottom + GAP); left = rect.left + rect.width / 2 - tw / 2; break;
  }
  left = Math.max(8, Math.min(W - tw - 8, left));
  top  = Math.max(8, Math.min(H - th - 8, top));

  // Final safety net: if the tooltip still overlaps the target rectangle,
  // shove it away in the direction with the most room.
  const overlaps = !(top + th < rect.top - 2 || top > rect.bottom + 2 ||
                     left + tw < rect.left - 2 || left > rect.right + 2);
  if (overlaps) {
    if (space.bottom >= space.top && space.bottom + th < H) {
      top = Math.min(H - th - 8, rect.bottom + GAP);
    } else if (space.top + th < H) {
      top = Math.max(8, rect.top - th - GAP);
    }
  }

  return { top, left, width: tw };
}

export default function GuidedTour({ user }) {
  const role = user?.role;
  const userId = user?.id;
  const steps = (role && STEPS_BY_ROLE[role]) || [];
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState(null);          // target bounding rect
  const [notFound, setNotFound] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const findTimer = useRef(null);

  const onDashboard = role && location.pathname.startsWith(`/${role}/dashboard`);

  // Auto-start on dashboard
  useEffect(() => {
    if (!role || !userId || steps.length === 0) return;
    const done = localStorage.getItem(guidedTourKey(userId, role));
    const skipped = localStorage.getItem(guidedTourSkipKey(userId, role));
    if (onDashboard && !done && !skipped) { setStepIdx(0); setOpen(true); }
  }, [role, userId, onDashboard, steps.length]);

  // Manual launcher
  useEffect(() => {
    const handler = () => { setStepIdx(0); setOpen(true); };
    window.addEventListener('guided-tour:start', handler);
    return () => window.removeEventListener('guided-tour:start', handler);
  }, []);

  // On every step change → navigate (if needed) then locate target
  const step = open ? steps[stepIdx] : null;
  useEffect(() => {
    if (!open || !step) { setRect(null); setNotFound(false); return; }
    // If route differs, navigate first; the rect-search effect re-runs after pathname change
    const targetPath = step.to.split('?')[0];
    if (targetPath && location.pathname.split('?')[0] !== targetPath) {
      navigate(step.to);
      return; // wait for re-render
    }
    setRect(null); setNotFound(false);
    if (findTimer.current) clearTimeout(findTimer.current);
    let tries = 0;
    const tick = () => {
      const el = document.querySelector(`[data-testid="${step.targetTestId}"]`);
      if (el) {
        try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch {}
        // wait one more frame for scroll to settle
        requestAnimationFrame(() => {
          const r = el.getBoundingClientRect();
          setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        });
        return;
      }
      tries += 1;
      if (tries < 15) { findTimer.current = setTimeout(tick, 100); }
      else { setNotFound(true); }
    };
    findTimer.current = setTimeout(tick, 150);
    return () => { if (findTimer.current) clearTimeout(findTimer.current); };
  }, [open, stepIdx, location.pathname, step?.targetTestId, step?.to]);

  // Re-position on resize/scroll (best-effort)
  useEffect(() => {
    if (!open || !rect) return;
    const handler = () => {
      const el = document.querySelector(`[data-testid="${step.targetTestId}"]`);
      if (el) { const r = el.getBoundingClientRect(); setRect({ top: r.top, left: r.left, width: r.width, height: r.height }); }
    };
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => { window.removeEventListener('resize', handler); window.removeEventListener('scroll', handler, true); };
  }, [open, rect, step?.targetTestId]);

  // BUG #22 — server-side persistence. Hydrate tour state from backend on
  // mount so it survives fresh browsers / incognito / localStorage clears.
  useEffect(() => {
    if (!userId) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/settings/ui`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (cancelled || !d) return;
        if (d.tour_completed) localStorage.setItem(guidedTourKey(userId, role), '1');
        if (d.tour_skipped) localStorage.setItem(guidedTourSkipKey(userId, role), '1');
        // If either flag is true, ensure any currently open tour closes.
        if (d.tour_completed || d.tour_skipped) setOpen(false);
      })
      .catch(() => { /* offline / not signed in — fall back to localStorage */ });
    return () => { cancelled = true; };
  }, [userId, role]);

  const persistServer = (flags) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/settings/ui`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(flags),
      }).catch(() => {});
    } catch (_) { /* ignore */ }
  };

  const finish = useCallback(() => {
    localStorage.setItem(guidedTourKey(userId, role), '1');
    localStorage.removeItem(guidedTourSkipKey(userId, role));
    persistServer({ tour_completed: true });
    setOpen(false);
    // Batch 1A correction — after the final step, show a completion state
    // only for the creator role. Other roles get no modal (previous
    // behaviour preserved).
    if (role === 'creator') setShowCompletion(true);
  }, [userId, role]);
  const skip = useCallback(() => {
    localStorage.setItem(guidedTourSkipKey(userId, role), '1');
    persistServer({ tour_skipped: true });
    setOpen(false);
  }, [userId, role]);

  // Batch 1A correction — creator tour completion card.
  if (showCompletion) {
    return (
      <div data-testid="tour-completion-overlay" style={{ position: 'fixed', inset: 0, zIndex: 5000 }}>
        <div style={{ position: 'absolute', inset: 0, background: SCRIM }} />
        <div data-testid="tour-completion-card" style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: 420, maxWidth: 'calc(100vw - 24px)',
          background: '#FFFFFF', borderRadius: theme.radius.card,
          padding: '24px 26px', boxShadow: '0 24px 70px rgba(0,0,0,0.30)',
        }}>
          <h3 style={{ fontSize: 20, fontWeight: 800, color: theme.colors.text.primary, margin: '0 0 8px', fontFamily: theme.fonts.heading }}>
            You&apos;re Ready to Get Started
          </h3>
          <p style={{ fontSize: 13, color: theme.colors.text.secondary, lineHeight: 1.55, margin: '0 0 18px' }}>
            You&apos;ve completed the Creator tour. You can begin building your first Kickback now or return to your dashboard and explore the platform.
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              data-testid="tour-completion-create-kickback"
              onClick={() => { setShowCompletion(false); navigate('/creator/portfolios/new'); }}
              style={{
                flex: '1 1 auto', padding: '10px 14px', border: 'none', cursor: 'pointer',
                borderRadius: theme.radius.sm, background: theme.colors.primary, color: '#FFF',
                fontWeight: 700, fontSize: 13,
              }}>
              Create My First Kickback
            </button>
            <button
              data-testid="tour-completion-dashboard"
              onClick={() => { setShowCompletion(false); navigate('/creator/dashboard'); }}
              style={{
                flex: '1 1 auto', padding: '10px 14px', cursor: 'pointer',
                borderRadius: theme.radius.sm, background: '#FFF',
                color: theme.colors.text.primary, fontWeight: 700, fontSize: 13,
                border: `1px solid ${theme.colors.borders}`,
              }}>
              Go to Dashboard
            </button>
          </div>
          <button
            data-testid="tour-completion-replay"
            onClick={() => setShowCompletion(false)}
            style={{
              marginTop: 14, background: 'transparent', border: 'none', cursor: 'pointer',
              fontSize: 12, color: theme.colors.text.muted, fontWeight: 600, padding: 0,
            }}>
            Replay Tour Later
          </button>
        </div>
      </div>
    );
  }

  if (!open || !step) return null;

  const tooltipPos = rect ? computeTooltipPos(rect, step.placement || 'bottom')
                          : { top: window.innerHeight / 2 - 90, left: window.innerWidth / 2 - 180, width: 360 };
  const isLast = stepIdx === steps.length - 1;

  return (
    <div data-testid="guided-tour-overlay" style={{ position: 'fixed', inset: 0, zIndex: 5000, pointerEvents: 'none' }}>
      {/* Scrim */}
      <div style={{ position: 'absolute', inset: 0, background: SCRIM, pointerEvents: 'auto' }} />
      {/* Highlight box */}
      {rect && (
        <div data-testid="guided-tour-highlight"
          style={{
            position: 'absolute',
            top: rect.top - 6, left: rect.left - 6,
            width: rect.width + 12, height: rect.height + 12,
            border: `3px solid ${theme.colors.primary}`,
            borderRadius: 10,
            boxShadow: `0 0 0 9999px ${SCRIM}`,
            transition: 'all 0.18s ease-out',
            pointerEvents: 'none',
          }}
        />
      )}
      {/* Tooltip */}
      <div data-testid="guided-tour-modal" style={{
          position: 'absolute', top: tooltipPos.top, left: tooltipPos.left, width: tooltipPos.width,
          background: '#FFFFFF', borderRadius: theme.radius.card, padding: '20px 22px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.30)', pointerEvents: 'auto',
        }}>
        <button data-testid="guided-tour-close" onClick={skip}
          style={{ position: 'absolute', top: 12, right: 12, background: 'transparent', border: 'none', cursor: 'pointer', color: theme.colors.text.muted }}>
          <X size={16} />
        </button>
        <p data-testid="guided-tour-progress" style={{ fontSize: '11px', fontWeight: 700, color: theme.colors.primary, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '6px' }}>
          Step {stepIdx + 1} of {steps.length}
        </p>
        <h3 style={{ fontSize: '17px', fontWeight: 700, color: theme.colors.text.primary, fontFamily: theme.fonts.heading, margin: '0 0 6px' }}>
          {step.title}
        </h3>
        <p style={{ fontSize: '13px', color: theme.colors.text.secondary, lineHeight: 1.55, margin: 0 }}>
          {step.body}
        </p>
        {notFound && (
          <p data-testid="guided-tour-not-found" style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: theme.colors.status.warning, background: 'rgba(255,180,0,0.1)', padding: '6px 10px', borderRadius: theme.radius.sm }}>
            <AlertCircle size={14} /> We couldn’t find this item — click Next.
          </p>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '14px', borderTop: `1px solid ${theme.colors.borders}`, paddingTop: '12px' }}>
          <button data-testid="guided-tour-skip" onClick={skip}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '12px', color: theme.colors.text.muted, fontWeight: 600 }}>
            Skip Tour
          </button>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button data-testid="guided-tour-back" disabled={stepIdx === 0} onClick={() => setStepIdx(i => Math.max(0, i - 1))}
              style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 10px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}`, background: '#FFF', cursor: stepIdx === 0 ? 'not-allowed' : 'pointer', color: theme.colors.text.primary, fontSize: '12px', fontWeight: 600, opacity: stepIdx === 0 ? 0.5 : 1 }}>
              <ChevronLeft size={12} /> Back
            </button>
            {!isLast ? (
              <button data-testid="guided-tour-next" onClick={() => setStepIdx(i => Math.min(steps.length - 1, i + 1))}
                style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', borderRadius: theme.radius.sm, border: 'none', background: theme.colors.primary, color: '#FFF', cursor: 'pointer', fontSize: '12px', fontWeight: 700 }}>
                Next <ChevronRight size={12} />
              </button>
            ) : (
              <button data-testid="guided-tour-finish" onClick={finish}
                style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', borderRadius: theme.radius.sm, border: 'none', background: theme.colors.status.success, color: '#FFF', cursor: 'pointer', fontSize: '12px', fontWeight: 700 }}>
                <CheckCircle2 size={12} /> Finish
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
